/**
 * Whether an event has been canceled. Canceled events should never link out
 * to registration/event pages — the UI shows them as canceled instead.
 * @param {Object} event - The event object
 * @returns {boolean}
 */
export const isEventCanceled = (event) => event?.eventType === 'Canceled';

/**
 * Normalizes an event's tags into a flat array of label strings, regardless
 * of whether the source data used an array of strings, an array of
 * {name} objects, or a comma-separated string.
 * @param {Array|string} tags - Raw tags value from an event
 * @returns {string[]} Flat array of tag labels
 */
export const normalizeEventTags = (tags) => {
    if (Array.isArray(tags)) {
        return tags.map((tag) => (typeof tag === 'string' ? tag : tag?.name || '')).filter(Boolean);
    }
    if (typeof tags === 'string') {
        return tags.split(',').map((tag) => tag.trim()).filter(Boolean);
    }
    return [];
};

/**
 * Generates a URL-friendly slug for an event
 * @param {Object} event - The event object containing startDate, organization, and title
 * @returns {string} URL-friendly slug
 */
export const generateEventSlug = (event) => {
    const eventDate = parseEventDate(event.startDate) || new Date(0);
    return `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}-${String(eventDate.getDate()).padStart(2, '0')}-${encodeURIComponent(event.organization)}-${encodeURIComponent(event.title)}`.toLowerCase();
};

/**
 * Parses common event date formats in a Safari-safe way.
 * @param {Date|string|number} dateInput - Date-like input
 * @returns {Date|null} Parsed Date or null for invalid values
 */
export const parseEventDate = (dateInput) => {
    if (dateInput instanceof Date) {
        return Number.isNaN(dateInput.getTime()) ? null : new Date(dateInput);
    }

    if (typeof dateInput === 'string') {
        const trimmed = dateInput.trim();

        // Date-only values are interpreted as local dates to avoid UTC shifting.
        const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (dateOnlyMatch) {
            const year = Number(dateOnlyMatch[1]);
            const month = Number(dateOnlyMatch[2]);
            const day = Number(dateOnlyMatch[3]);
            const localDate = new Date(year, month - 1, day);
            return Number.isNaN(localDate.getTime()) ? null : localDate;
        }

        // Safari is stricter with "YYYY-MM-DD HH:mm:ss" than Chromium; normalize to ISO-like local time.
        const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
        const parsed = new Date(normalized);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }
    }

    const fallback = new Date(dateInput);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
};

/**
 * Creates a local YYYY-MM-DD key from a Date or date-like value.
 * @param {Date|string|number} dateInput - Date-like input
 * @returns {string|null} Local date key or null for invalid dates
 */
export const getLocalDateKey = (dateInput) => {
    const date = parseEventDate(dateInput);
    if (!date) {
        return null;
    }

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/**
 * Parses a slug to extract date information
 * @param {string} slug - The event slug
 * @returns {Object|null} Object containing year and month, or null if invalid
 */
export const parseEventSlug = (slug) => {
    try {
        const parts = slug.split('-');
        if (parts.length >= 3) {
            const year = parseInt(parts[0]);
            const month = parseInt(parts[1]);
            const day = parseInt(parts[2]);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day) && 
                month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                return { year, month, day };
            }
        }
        return null;
    } catch (error) {
        //console.error('Error parsing event slug:', error);
        return null;
    }
}; 